import React, { useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { appTheme } from "shared/theme";

const COLORS = {
  primary: appTheme.palette.green.main,
  secondary: appTheme.palette.gray.main,
  bg: appTheme.palette.white.main,
  softBg: appTheme.palette.gray.light ,
  border: "rgba(0,0,0,0.08)",
};

const textSx = {
  fontFamily: appTheme.typography?.body1?.fontFamily,
  color: COLORS.secondary,
  lineHeight: 1.75,
};

const subheadSx = {
  ...textSx,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: COLORS.primary,
  fontWeight: 700,
  mb: 1.25,
  fontSize: { xs: 12, sm: 13 },
};

const Title = ({ children }) => (
  <Typography
    variant="h4"
    component="h3"
    sx={{
      fontFamily:
        appTheme.typography?.h1?.fontFamily || appTheme.typography?.body1?.fontFamily,
      textTransform: "uppercase",
      color: COLORS.secondary,
      fontWeight: 800,
      lineHeight: 1.15,
      fontSize: { xs: 22, sm: 26, md: 30 },
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const ImageFrame = ({ src, alt, compact }) => (
  <Box
    sx={{
      width: "100%",
      borderRadius: { xs: 3, md: 4 },
      overflow: "hidden",
      border: `1px solid ${COLORS.border}`,
      backgroundColor: appTheme.palette.white?.main,
      boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      loading="lazy"
      sx={{
        display: "block",
        width: "100%",
        height: "auto",
        objectFit: "contain",
        maxHeight: compact
          ? { xs: 280, sm: 320, md: 360 }
          : { xs: 360, sm: 440, md: 520 },
      }}
    />
  </Box>
);

const StoreBadges = ({ center }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      mt: 3,
      flexWrap: "wrap",
      justifyContent: center ? "center" : "flex-start",
      alignItems: "center",
    }}
  >
    <Box
      component="a"
      href="https://apps.apple.com/au/app/7day-app/id6470282265?itsct=apps_box_link&itscg=30200"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Download on the App Store"
        src="/appleBadge.jpg"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>

    <Box
      component="a"
      href="https://play.google.com/store/apps/details?id=com.sevenday.mobile&pcampaignid=web_share7DAY"
      target="_blank"
      rel="noopener noreferrer"
      sx={{ lineHeight: 0, display: "inline-flex" }}
    >
      <Box
        component="img"
        alt="Get it on Google Play"
        src="/googleBadge.png"
        sx={{
          height: { xs: 44, sm: 50 },
          width: "auto",
          display: "block",
        }}
      />
    </Box>
  </Box>
);

const FeatureSection = ({ section, index }) => {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const reversed = mdUp ? Boolean(section.reverseOnDesktop) : false;

  const isCompact =
    section.image?.compact ||
    section.title.toLowerCase().includes("notifications") ||
    section.title.toLowerCase().includes("reporting");

  return (
    <Box
      component="section"
      sx={{
        borderRadius: { xs: 4, md: 6 },
        border: `1px solid ${COLORS.border}`,
        background:
          index % 2 === 0
            ? "linear-gradient(180deg, rgba(92,181,179,0.08) 0%, rgba(255,255,255,1) 55%)"
            : "linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(255,255,255,1) 55%)",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, sm: 4, md: 5 },
      }}
    >
      <Grid
        container
        spacing={{ xs: 3, md: 6 }}
        alignItems="center"
        direction={reversed ? "row-reverse" : "row"}
      >
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: reversed ? "flex-start" : "flex-end" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: {
                  xs: 520,
                  sm: 640,
                  md: isCompact ? 560 : 680,
                },
              }}
            >
              <ImageFrame
                src={section.image.src}
                alt={section.image.alt}
                compact={isCompact}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              maxWidth: 680,
              mx: { xs: "auto", md: 0 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography sx={subheadSx}>{section.kicker}</Typography>

            <Title>{section.title}</Title>

            <Box sx={{ mt: 2 }}>
              {section.paragraphs.map((p, i) => (
                <Typography
                  key={i}
                  sx={{
                    ...textSx,
                    mb: i === section.paragraphs.length - 1 ? 0 : 1.75,
                    fontSize: { xs: 14.5, sm: 15.5, md: 16 },
                  }}
                >
                  {p}
                </Typography>
              ))}
            </Box>

            {section.hasStoreBadges && <StoreBadges center={!mdUp} />}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const ProductContentHosts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const headerVariant = useMemo(
    () => (isMobile ? "h3" : isTablet ? "h2" : "h1"),
    [isMobile, isTablet],
  );

  const sections = [
    {
      title: "Manage and grow your experiences—without the admin headache",
      kicker: "One app. Every experience. Sorted.",
      paragraphs: [
        "With the 7DAY app, hosts can list, price, and manage everything from homes and venues to boats and vehicles, all in one place.",
        "Control availability, bookings, and guest communication with a smooth, streamlined system built for busy hosts.",
        "Available on web and mobile, 7DAY makes hosting effortless—so you can spend less time managing and more time earning.",
      ],
      image: { src: "/imgs/classic/host/listingPg.png", alt: "Listings" },
      hasStoreBadges: true,
    },
    {
      title: "Offer add-on experiences",
      kicker: "Give guests more ways to experience your destination.",
      paragraphs: [
        "Create add-on experiences that guests can book alongside their reservation. From boat charters and luxury vehicles to team-building activities, guided tours, and wellness experiences,  add-ons help you showcase everything your business has to offer.",
        "Guests can easily discover and add these experiences during the booking journey, creating a more memorable stay while increasing the value of every reservation. Manage your add-ons from one place and offer guests a complete experience beyond the accommodation itself.",
      ],
      image: {
        src: "/imgs/classic/host/addOn.png",
        alt: "Host add-on experiences",
      },
      reverseOnDesktop: true,
    },
    {
      title: "Seasonal Pricing Rates",
      kicker: "Maximise your earnings all year round with seasonal pricing.",
      paragraphs: [
        "Set different rates for peak periods, weekends, holidays, or quieter months—so your experience is always priced right for demand.",
        "Update once, let it run automatically, and stay in control of your revenue without constantly adjusting prices.",
      ],
      image: { src: "/imgs/classic/host/seasonalRates.png", alt: "Seasonal rates" },
      reverseOnDesktop: false,
    },
    {
      title: "Manage your bookings",
      kicker: "Stay in control of your guests bookings—past, present, and future.",
      paragraphs: [
        "With our built-in calendar, you can easily view and manage all trip details, update upcoming bookings, and export or share plans with your team.",
        "7DAY has in in-built calendar and also integrates with Outlook, Google Calendar, and Microsoft 365, so everything stays in sync—no double handling required.",
      ],
      image: { src: "/imgs/classic/host/reservationList.png", alt: "Reservation List" },
      reverseOnDesktop: true,
    },
    {
      title: "Daily Visitor Overview",
      kicker: "See who’s coming and going—before they even arrive.",
      paragraphs: [
        "With Daily Visitor Overview, hosts can view every guest and attendee booked for the day in one clear list, making it easy to manage busy office spaces with multiple check-ins, overlaps, and back-to-back visits.",
        "Perfect for staying organised, keeping security smooth, and running your space like clockwork.",
      ],
      image: { src: "/imgs/classic/host/dailyVisitors.png", alt: "Daily Visitor List" },
      reverseOnDesktop: false,
    },
    {
      title: "Face & Vehicle Recognition Access (Keyless Check-In)",
      kicker:
        "Smoother arrivals, fewer headaches, and a premium first impression every time.",
      paragraphs: [
        "Hosts have the option to make check-ins effortless with keyless entry powered by face and vehicle recognition. Guests arrive, access is granted instantly, and your experience stays secure—no key handovers, no lockboxes, no “where’s the code?” texts.",
      ],
      image: {
        src: "/imgs/classic/guests/facerec.png",
        alt: "Face and vehicle recognition",
        compact: true,
      },
      reverseOnDesktop: true,
    },
    {
      title: "Real-time notifications",
      kicker: "Stay informed without lifting a finger.",
      paragraphs: [
        "Our in-app notification center keeps you updated on all booking activity as it happens—from approvals and declines to cancellations.",
        "With real-time alerts, you’ll spend less time chasing updates and more time focusing on what matters.",
      ],
      image: {
        src: "/imgs/classic/host/notifications.jpg",
        alt: "Notifications",
        compact: true,
      },
      reverseOnDesktop: false,
    },
    {
      title: "User Management",
      kicker: "Easily manage who can access and use 7DAY across your team.",
      paragraphs: [
        "Invite staff securely, assign roles, and control access levels with ease. Whether you’re adding new team members or adjusting permissions, our user management tools put you in control.",
        "With three security profile types, you decide who can view, book, or manage guest bookings—keeping everything organised and secure.",
      ],
      image: { src: "/imgs/classic/host/UserManagement.png", alt: "User management" },
      reverseOnDesktop: true,
    },
    {
      title: "Reporting",
      kicker: "Know what’s working—at a glance.",
      paragraphs: [
        "With 7DAY Reporting, hosts can track bookings, revenue, performance trends, and guest activity in one clear dashboard. See what’s driving demand, spot your busiest periods, and make smarter pricing and availability decisions faster.",
        "From monthly summaries to financial year breakdowns, our reporting tools help you tailor future perks and make smarter decisions—backed by real data.",
      ],
      image: { src: "/imgs/classic/host/reservationReport.png", alt: "Reporting", compact: true },
      reverseOnDesktop: false,
    },
    {
      title: "In-App Messaging",
      kicker: "Connect directly with guests —before or after they book.",
      paragraphs: [
        "Our built-in messenger makes it easy to ask questions, confirm details, or coordinate plans with the guest and internal team members. Whether you're responding to a listing enquiry or discussing an upcoming reservation, everything stays in one place—secure, simple, and streamlined.",
        "No more chasing emails or phone calls—just clear, real-time communication right inside the 7DAY app.",
      ],
      image: { src: "/imgs/classic/host/chat.jpg", alt: "In-app messaging" },
      reverseOnDesktop: true,
    },
    {
      title: "Customised Message Templates",
      kicker: "Look ultra-organised without doing the extra work.",
      paragraphs: [
        "Send the right message at the right time—automatically. With 7DAY’s custom message templates, hosts can create personalised guest messages that trigger when a reservation is confirmed, cancelled, before arrival, after checkout, or at any custom timeframe you choose.",
        "Set it once, keep your communication consistent, and give every guest a smooth, professional experience—without lifting a finger each time.",
      ],
      image: { src: "/imgs/classic/host/messagetemplate.png", alt: "Message template" },
      reverseOnDesktop: false,
    },
    {
      title: "Contractor Job Requests",
      kicker: "Need a cleaner, caterer, or massage therapist?",
      paragraphs: [
        "Send job requests to external contractors straight from 7DAY. Create a request in seconds, include the booking details, timing, and notes, then track everything in one place.",
        "Less back-and-forth, smoother service delivery, and a better guest experience—every time.",
      ],
      image: { src: "/imgs/classic/host/jobrequest.jpg", alt: "Job Request" },
      reverseOnDesktop: true,
    },
    {
      title: "Guest Expense Submissions",
      kicker: "Charge the extras in seconds — with receipts, clarity, and zero awkwardness.",
      paragraphs: [
        "Add extras without the awkward follow-up. With Guest Expense Submissions, hosts can easily submit additional charges like minibar items, groceries, alcohol, or restocks directly to the guest—complete with notes and receipts.",
        "Keep everything clear, professional, and tracked in one place.",
      ],
      image: { src: "/imgs/classic/host/expense.jpg", alt: "Expenses" },
      reverseOnDesktop: false,
    },
    {
      title: "Custom Host Policies & Guest Consent",
      kicker: "Set the rules your way.",
      paragraphs: [
        "With 7DAY, hosts can create their own booking policies that guests must review and agree to before they confirm a reservation—so expectations are clear from the start.",
        "You can also offer optional cancellation choices, giving guests flexibility while protecting your revenue. Clear, compliant, and stress-free for everyone.",
      ],
      image: { src: "/imgs/classic/host/policies.jpg", alt: "Policies" },
      reverseOnDesktop: true,
    },
    {
      title: "Admin Centre",
      kicker: "All the admin power — none of the admin pain!",
      paragraphs: [
        "Run everything from one place. The 7DAY Admin Centre gives you full control over your account—manage users and access, add contractors, set billing exclusions, handle billing and fees, customise notification preferences, and more.",
        "It’s your command centre for keeping the whole operation organised, secure, and running smoothly.",
      ],
      image: { src: "/imgs/classic/host/admin center.jpg", alt: "Admin Centre" },
      reverseOnDesktop: false,
    },
    {
      title: "Smart Integrations",
      kicker: "Built to Save You Time!",
      paragraphs: [
        "7DAY connects with the tools you already use to keep everything running smoothly. Sync bookings with Outlook and Google Calendar, draft bills sent via the Xero network without manually entering revenue, and level up access control with Nirovision for facial recognition entry and vehicle access to experiences.",
        "Less admin, more automation, and a seriously seamless setup.",
      ],
      image: { src: "/imgs/classic/integrations.jpg", alt: "Integrations", compact: true },
      reverseOnDesktop: true,
    },
  ];

  return (
    <Box sx={{ backgroundColor: COLORS.bg }}>
      <Box
        sx={{
          background:
            "radial-gradient(900px 500px at 15% 0%, rgba(92,181,179,0.16) 0%, rgba(255,255,255,0) 60%), radial-gradient(800px 420px at 85% 0%, rgba(0,0,0,0.05) 0%, rgba(255,255,255,0) 55%)",
        }}
      >
        <Container
          id="product"
          maxWidth="lg"
          sx={{
            pt: { xs: 14, md: 20 },
            pb: { xs: 6, md: 10 },
          }}
        >
          <Box sx={{ textAlign: "center", maxWidth: 980, mx: "auto" }}>
            <Typography
              component={headerVariant}
              variant={headerVariant}
              sx={{
                color: COLORS.secondary,
                fontFamily:
                  appTheme.typography?.h1?.fontFamily ||
                  appTheme.typography?.body1?.fontFamily,
                textTransform: "uppercase",
                fontWeight: 900,
                letterSpacing: "0.06em",
                lineHeight: 1.05,
                fontSize: {
                  xs: 34,
                  sm: 44,
                  md: 56,
                },
              }}
            >
              Host Features
            </Typography>

            <Typography
              variant="h6"
              component="h2"
              sx={{
                ...textSx,
                mt: 1.25,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                color: COLORS.primary,
                fontWeight: 800,
                fontSize: { xs: 12, sm: 13, md: 14 },
              }}
            >
              The all-in-one home for hosting and managing experiences.
            </Typography>

            <Box
              sx={{
                mt: 3,
                height: 1,
                width: { xs: "100%", sm: 640 },
                mx: "auto",
                backgroundColor: COLORS.border,
              }}
            />
          </Box>

          <Box
            sx={{
              mt: { xs: 5, md: 7 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, md: 4 },
            }}
          >
            {sections.map((section, index) => (
              <FeatureSection key={section.title} section={section} index={index} />
            ))}
          </Box>
        </Container>
      </Box>

      <Box
        sx={{
          backgroundColor: COLORS.softBg,
          borderTop: `1px solid ${COLORS.border}`,
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 3 },
              alignItems: { xs: "center", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography
                sx={{
                  fontFamily:
                    appTheme.typography?.h1?.fontFamily ||
                    appTheme.typography?.body1?.fontFamily,
                  textTransform: "uppercase",
                  fontWeight: 900,
                  color: COLORS.secondary,
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  lineHeight: 1.2,
                }}
              >
                Ready to host with less admin and more momentum?
              </Typography>
              <Typography sx={{ ...textSx, mt: 1, maxWidth: 680 }}>
                Web + mobile, built for busy hosts managing real bookings in real time.
              </Typography>
            </Box>

            <StoreBadges center />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
